import React, { Component } from 'react';

import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Tooltip from '@atlaskit/tooltip';

import InlineEdit from '@qality/common-components/src/molecules/inline-edit';
import { FEATURE_FLAG } from '@qality/common-config';
import { TestStep, TestStepsView, User } from '@qality/common-model';
import {
  StorageOfUniqueDialogs,
  TestStepService,
} from '@qality/common-service';
import {
  InputUtils,
  PasteAttachmentsUtils,
  StringUtils,
} from '@qality/common-utils';

import TestCaseBoxAttachments from './TestCaseBoxAttachments';
import { StepContainerStyled } from './TestCaseBoxStep.styled';
import { TestStepMenu } from './TestStepMenu';
import './_test-case-box-step.scss';

type Props = {
  canEdit: boolean;
  testStep: TestStep;
  view: TestStepsView;
  testStepIndex: number;
  onDeleteTestStep?: (testStepIndex: number, testStep: TestStep) => void;
  onUpdateTestStep?: (
    testStepIndex: number,
    testStep: TestStep,
    isNewTestStep?: boolean
  ) => void;
  testCaseIssueKey?: string;
  canDeleteAllAttachment?: boolean;
  canCreateAttachment?: boolean;
  canDeleteOwnAttachment?: boolean;
  currentUser?: User;
  dragHandleProps?: any;
  snapshot?: any;
  cloneTestStep?: (testStepIndex: number) => Promise<void | boolean>;
  stepSaveFailed?: boolean;
  getShouldDeleteAttachment?: (fileId: number, testStep: TestStep) => boolean;
};

type State = {
  loading: boolean;
  isHovering: boolean;
  testCaseIssueKey: string;
  pastedAttachments: Array<File>;
};

export default class TestCaseBoxStep extends Component<Props, State> {
  static defaultProps = {
    isExecution: false,
    stepSaveFailed: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      isHovering: false,
      testCaseIssueKey: '',
      pastedAttachments: null,
    };
  }

  handleOnMouseEnter = (): void => {
    this.setState({ isHovering: true });
  };

  handleOnMouseLeave = (): void => {
    this.setState({ isHovering: false });
  };

  onConfirm = (field: string, value: string, shouldUpdate: boolean): void => {
    const valueWithEmptyLines = InputUtils.keepEmptyLines(value);
    const { onUpdateTestStep, testStepIndex, testStep } = this.props;
    if (onUpdateTestStep) {
      const updatedTestStep = {
        ...testStep,
        [field]: valueWithEmptyLines,
      } as TestStep;
      shouldUpdate && TestStepService.updateTestStep(updatedTestStep);
      onUpdateTestStep(testStepIndex, {
        ...testStep,
        [field]: valueWithEmptyLines,
      });
    }
  };

  createInlineEditableTextfield = (
    defaultValue: string,
    field: string
  ): JSX.Element => {
    return (
      <InlineEdit
        key={defaultValue}
        text={defaultValue}
        onConfirm={(value, shouldUpdate) =>
          this.onConfirm(field, value, shouldUpdate)
        }
        length={5000}
      />
    );
  };

  deleteStep = (): void => {
    const { testStep, onDeleteTestStep } = this.props;
    TestStepService.deleteTestStep(testStep);
    if (onDeleteTestStep) {
      onDeleteTestStep(this.props.testStepIndex, testStep);
    }
  };

  onUpdateAttachment = (testStep: TestStep): void => {
    const { onUpdateTestStep } = this.props;
    if (onUpdateTestStep) {
      TestStepService.updateTestStep(testStep);
      onUpdateTestStep(this.props.testStepIndex, testStep);
    }
  };

  onLoading = (value: boolean): void => {
    this.setState({ loading: value });
  };

  getColumnWidth = (): string => {
    return this.props.view.isDetailView ? '27%' : '50%';
  };

  handleOnPaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    const isPasteEnabled = [
      FEATURE_FLAG.isPremium,
      this.props.canEdit,
      this.props.canCreateAttachment,
      this.props.view.isDetailView,
    ].every(Boolean);

    if (isPasteEnabled) {
      this.setState({
        pastedAttachments:
          PasteAttachmentsUtils.convertPastedFiledToAttachments(e),
      });
    }
  };

  clearPastedAttachments = (): void => {
    this.setState({
      pastedAttachments: null,
    });
  };

  render(): JSX.Element {
    const { isDetailView } = this.props.view;
    const columnWidth = this.getColumnWidth();
    const attachmentBoxWidth = `${100 - 3 * parseFloat(columnWidth)}%`;

    return (
      <StepContainerStyled
        isHovered={this.state.isHovering}
        isDragged={this.props.snapshot?.isDragging}
        isStepSaveFailed={this.props.stepSaveFailed}
        onMouseEnter={this.handleOnMouseEnter}
        onMouseLeave={this.handleOnMouseLeave}
        data-testid='test-step-box-wrapper'
        onPaste={this.handleOnPaste}
      >
        {this.props.stepSaveFailed ? (
          <div className='test-case-box-warning-icon-wrapper'>
            <Tooltip
              content="Your step wasn't saved correctly. Please use the retry button to try again."
              position='bottom'
            >
              <ErrorIcon
                label='warning-icon'
                primaryColor='var(--ds-icon-danger, var(--ds-fb-icon-danger))'
              />
            </Tooltip>
          </div>
        ) : (
          <div className='test-case-box-dynamic-handler'>
            <DynamicStepDragHandler
              dragHandleProps={this.props.dragHandleProps}
              showDragHandler={
                this.props.snapshot.isDragging ||
                (this.state.isHovering &&
                  this.props.canEdit &&
                  this.props.dragHandleProps)
              }
              stepNumber={this.props.testStepIndex + 1}
            />
          </div>
        )}

        <div className='test-step-data-wrapper hide-focus-wrapper'>
          <div className='column-row' style={{ width: columnWidth }}>
            <div>
              {this.props.canEdit ? (
                this.createInlineEditableTextfield(
                  this.props.testStep.step,
                  'step'
                )
              ) : (
                <p
                  className='text-wrapper'
                  dangerouslySetInnerHTML={{
                    __html: StringUtils.toHTML(this.props.testStep.step),
                  }}
                />
              )}
            </div>
          </div>
          {isDetailView && (
            <div className='column-row' style={{ width: columnWidth }}>
              <div>
                {this.props.canEdit ? (
                  this.createInlineEditableTextfield(
                    this.props.testStep.data,
                    'data'
                  )
                ) : (
                  <p
                    className='text-wrapper'
                    dangerouslySetInnerHTML={{
                      __html: StringUtils.toHTML(this.props.testStep.data),
                    }}
                  />
                )}
              </div>
            </div>
          )}
          <div className='column-row' style={{ width: columnWidth }}>
            <div>
              {this.props.canEdit ? (
                this.createInlineEditableTextfield(
                  this.props.testStep.result,
                  'result'
                )
              ) : (
                <p
                  className='text-wrapper'
                  dangerouslySetInnerHTML={{
                    __html: StringUtils.toHTML(this.props.testStep.result),
                  }}
                />
              )}
            </div>
          </div>
          {isDetailView && (
            <div className='column-row' style={{ width: attachmentBoxWidth }}>
              <div>
                <TestCaseBoxAttachments
                  testStep={this.props.testStep}
                  onUpdateAttachment={this.onUpdateAttachment}
                  issueKey={this.props.testCaseIssueKey}
                  onLoading={this.onLoading}
                  loading={this.state.loading}
                  canEdit={this.props.canEdit}
                  canCreateAttachment={this.props.canCreateAttachment}
                  canDeleteAllAttachment={this.props.canDeleteAllAttachment}
                  canDeleteOwnAttachment={this.props.canDeleteOwnAttachment}
                  currentUser={this.props.currentUser}
                  pastedAttachments={this.state.pastedAttachments}
                  shouldRefreshAttachments={true}
                  getShouldDeleteAttachment={
                    this.props.getShouldDeleteAttachment
                  }
                  clearPastedAttachments={this.clearPastedAttachments}
                  isHovered={this.state.isHovering}
                />
              </div>
            </div>
          )}
        </div>
        {!this.props.stepSaveFailed && (
          <div className='step-menu-wrapper'>
            {this.state.isHovering && this.props.canEdit && (
              <TestStepMenu
                onCloneTestStep={() =>
                  this.props.cloneTestStep?.(this.props.testStepIndex)
                }
                onDeleteTestStep={() =>
                  StorageOfUniqueDialogs.get(
                    'DeleteTestStepDialog'
                  ).openDeleteStepDialog(this.deleteStep)
                }
              />
            )}
          </div>
        )}
      </StepContainerStyled>
    );
  }
}

const DynamicStepDragHandler = ({
  stepNumber,
  showDragHandler,
  dragHandleProps,
}: any) => {
  if (showDragHandler) {
    return (
      <div
        className='test-case-box-dynamic-handler-icon-container'
        {...dragHandleProps}
        tabIndex={-1}
      >
        <DragHandlerIcon size='large' label='DragHandlerIcon' />
      </div>
    );
  }

  return (
    <div className='test-case-box-step-id-digit'>
      <span>{stepNumber}</span>
    </div>
  );
};
